import { useCallback, useMemo, useState, type ComponentProps } from "react";
import { Box, FormControl, useTheme } from "@mui/material";
import { useField } from "formik";
import { useTranslation } from "react-i18next";

import type {
  AvailableOrganisationUnbranded,
  OrganisationRole,
  TranslationKeyAndOptions,
} from "@thalamos/vision-common";

import { Typeahead } from "../../Typeahead/Typeahead.js";
import { useOrganisations } from "../OrganisationDropdown/OrganisationContext.js";

export type DropdownOutput =
  | {
      value: string;
      valueLabel: string;
    }
  | "";

export type OrganisationTypeaheadProps = {
  fieldName: string;
  label?: TranslationKeyAndOptions;
  filterByRoles?: OrganisationRole[];
  /**
   * When true, organisations flagged as `excludeAsIncidentLocation` will be
   * included in the typeahead options. Defaults to false.
   */
  includeExcludedAsIncidentLocation?: boolean;
} & Pick<ComponentProps<typeof Typeahead>, "autoFocus">;

export function OrganisationTypeahead({
  fieldName,
  label,
  filterByRoles,
  includeExcludedAsIncidentLocation = false,
  autoFocus,
}: OrganisationTypeaheadProps) {
  const theme = useTheme();
  const organisationContext = useOrganisations();
  const { t } = useTranslation();

  const [field, _meta, helpers] = useField<DropdownOutput>(fieldName);

  const [selectedValue, setSelectedValue] = useState<string | null>(
    field.value === "" ? null : field.value.valueLabel,
  );

  const [sectorIdInput, setSectorIdInput] = useState<string | null>(
    field.value === "" ? null : selectedValue,
  );

  const [errorMessage, setErrorMessage] = useState<string>("");

  const filteredOptions = useMemo(() => {
    let options = filterByRoles
      ? organisationContext.organisations.filter((option) =>
          filterByRoles.includes(option.role),
        )
      : organisationContext.organisations;

    if (!includeExcludedAsIncidentLocation) {
      options = options.filter((option) => !option.excludeAsIncidentLocation);
    }

    return [...options].sort((a, b) =>
      a.name.localeCompare(b.name, "en-GB", { sensitivity: "base" }),
    );
  }, [
    filterByRoles,
    includeExcludedAsIncidentLocation,
    organisationContext.organisations,
  ]);

  const onChangeInput = useCallback(
    (inputValue: string) => {
      const runAsync = async () => {
        const match = filteredOptions.find(
          (sectors) =>
            sectors.name.toLocaleLowerCase() === inputValue.toLocaleLowerCase(),
        );
        if (match) {
          const newValue = {
            value: match.id,
            valueLabel: match.name,
          };
          setSelectedValue(match.name);
          await helpers.setValue(newValue);
          await helpers.setTouched(true);
          setErrorMessage("");
        } else {
          void helpers.setValue("");
          if (inputValue.length < 1) {
            setErrorMessage("");
          } else {
            setErrorMessage(t("createIncident.invalidGeographicUnit"));
          }
        }
        setSectorIdInput(inputValue);
      };
      void runAsync();
    },
    [filteredOptions, helpers],
  );

  const getSectorIdOptions = useMemo(() => {
    if (!sectorIdInput || sectorIdInput.length < 1) {
      return [];
    }
    return filteredOptions
      .filter((options) =>
        options.name.toLowerCase().includes(sectorIdInput.toLowerCase()),
      )
      .slice(0, 5);
  }, [sectorIdInput, filteredOptions]);

  return (
    <Box sx={{ paddingBottom: theme.spacing(3) }}>
      <FormControl fullWidth data-testid={`formField-${fieldName}`}>
        <Typeahead
          label={label}
          name={fieldName}
          fieldName={fieldName}
          options={getSectorIdOptions}
          optionsKey="id"
          getOptionLabel={(option: string | AvailableOrganisationUnbranded) => {
            if (typeof option === "string") {
              return option;
            }
            return option.name;
          }}
          onInputChange={onChangeInput}
          inputValue={sectorIdInput ?? ""}
          errorMessage={errorMessage}
          autoFocus={autoFocus}
        />
      </FormControl>
    </Box>
  );
}
